/**
 * 網站雛型管理 API Service（porsche 2026-08-13 · 目錄上傳 pivot）。
 * 建 metadata → 逐檔上傳（每檔小·續傳覆寫），繞開單一大 zip 請求的 nginx/RAM/逾時/連線池限制。
 */
import axiosInstance from '../../../../api/axios';

const BASE = '/api/ee/website/prototypes';

export interface WebsitePrototype {
  id: number;
  tenant_id: number;
  name: string;
  description?: string | null;
  version: number;
  entry_html: string;
  extracted_dir: string;
  uploaded_by: number;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export const listPrototypes = async (): Promise<WebsitePrototype[]> =>
  (await axiosInstance.get<WebsitePrototype[]>(BASE)).data;

// 建立 metadata（無檔）→ 拿 id 後逐檔上傳
export const createPrototype = async (name: string, description: string): Promise<WebsitePrototype> => {
  const fd = new FormData();
  fd.append('name', name);
  if (description) fd.append('description', description);
  return (await axiosInstance.post<WebsitePrototype>(BASE, fd, MULTIPART)).data;
};

// 逐檔上傳（relative_path = webkitRelativePath；覆寫 = 續傳）
export const uploadOneFile = async (id: number, relativePath: string, file: File): Promise<void> => {
  const fd = new FormData();
  fd.append('relative_path', relativePath);
  fd.append('file', file);
  await axiosInstance.post(`${BASE}/${id}/files`, fd, MULTIPART);
};

export const updatePrototype = async (
  id: number, name: string, description: string, replace: boolean,
): Promise<WebsitePrototype> => {
  const fd = new FormData();
  fd.append('name', name);
  fd.append('description', description);
  fd.append('replace', String(replace));
  return (await axiosInstance.put<WebsitePrototype>(`${BASE}/${id}`, fd, MULTIPART)).data;
};

export const deletePrototype = async (id: number): Promise<void> => {
  await axiosInstance.delete(`${BASE}/${id}`);
};

// 公開 static serve URL（demo 網站對外；免 auth）→ 直接另開新頁
export const previewPublicUrl = (p: WebsitePrototype): string =>
  p.entry_html ? `/prototypes/${p.extracted_dir}/${p.entry_html}` : `/prototypes/${p.extracted_dir}/`;

// 依序上傳整個目錄的檔案（含檔案級 retry；回報進度）。回傳失敗清單。
export const uploadDirectory = async (
  id: number, files: File[], onProgress: (done: number, total: number) => void,
): Promise<{ failed: string[] }> => {
  const failed: string[] = [];
  let done = 0;
  for (const f of files) {
    const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    let ok = false;
    for (let attempt = 0; attempt < 2 && !ok; attempt++) {
      try { await uploadOneFile(id, rel, f); ok = true; }
      catch { /* retry once */ }
    }
    if (!ok) failed.push(rel);
    done += 1;
    onProgress(done, files.length);
  }
  return { failed };
};
